import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { platformApi } from '../../api/endpoints/platform';
import { LevelEntitiesTable } from '../../pages/platform/tenants/levels/LevelEntitiesTable';
import {
    isLevelName,
    fallbackLevelDisplayName,
} from '../../pages/platform/tenants/levels/levelConfig';

/**
 * Pestaña GENÉRICA de un nivel del drill-down de un tenant
 * (`/platform/tenants/{tenantId}/levels/{level}`). Coexiste con las pestañas bespoke de
 * retailers/stores/groups (NO las reemplaza todavía — se agregan en paralelo para no romper la
 * navegación existente).
 *
 * `level` (LEVEL_1/2/3) sale del PATH y se valida con `isLevelName` (un nivel inválido → 404). El
 * `tenantId` también sale del PATH (no del user logueado) y se pasa por prop a la tabla.
 *
 * displayName del nivel — TD-WEBAPP-023 (resuelto): el nombre de negocio real de cada nivel vive en
 * `TenantDetail.levels[].displayName` (el backend ya lo enriquece; mismo dato que alimenta los tabs del
 * shell). Se carga el tenant con la MISMA queryKey del shell (`['platform','tenants',tenantId,'detail']`
 * → reusa cache, sin fetch extra), se busca el nivel que matchea el `level` del path y se usa su
 * `displayName`. Solo si el tenant aún no cargó (o el nivel no está en el detalle) se cae al fallback
 * genérico "Nivel N" (`fallbackLevelDisplayName`) — legible, no es el "LEVEL_1" crudo.
 *
 * LEVEL_2/LEVEL_3 requieren un `parentId` (el backend lo exige): la UX de selección de padre se
 * define en el brainstorm #3-a. Por ahora la pantalla soporta LEVEL_1 (sin padre); para LEVEL_2/3
 * muestra un aviso de que falta seleccionar el padre (no inventamos el selector acá).
 */
export const Route = createFileRoute(
    '/_authenticated/_layout/platform/tenants/$tenantId/levels/$level',
)({
    beforeLoad: ({ params }) => {
        if (!isLevelName(params.level)) throw notFound();
    },
    component: TenantLevel,
});

function TenantLevel() {
    const { tenantId, level } = Route.useParams();

    // Detalle del tenant con la MISMA queryKey que el shell (y las otras rutas del drill-down) →
    // dedupe/reuse de cache, sin fetch extra. Trae `levels[]` = los niveles de la app con su
    // displayName de negocio. La query se llama INCONDICIONAL (regla de hooks); el `enabled` no hace
    // falta porque `tenantId` viene del path (siempre presente).
    const { data: tenant } = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'detail'],
        queryFn: () => platformApi.getTenant(tenantId),
    });

    // El beforeLoad ya garantizó que `level` es un LevelName válido.
    if (!isLevelName(level)) return null;

    // displayName REAL del nivel desde `tenant.levels` (mismo dato que los tabs del shell). Fallback
    // genérico "Nivel N" SOLO si el tenant aún no cargó o el nivel no aparece en su detalle.
    const levelDisplayName =
        tenant?.levels?.find((lvl) => lvl.level === level)?.displayName ??
        fallbackLevelDisplayName(level);

    // DEC-031 / DEC-RBAC-027 (BYOID): el `requireExternalId` se deriva de la config del TENANT DEL DRILL-DOWN
    // (path), NO de la sesión del admin logueado. Si el nivel está en `externalScopeLevels` del tenant,
    // el backend exige y valida un `id` externo (`requireValidScopeId`) → el form debe pedir el campo
    // "ID". Sin esto, el form solo manda `displayName`, el backend recibe `id: undefined` y responde
    // "scopeId inválido: 1..128 chars".
    const requireExternalId = tenant?.externalScopeLevels?.includes(level) ?? false;

    // TODO(#3-a): LEVEL_2/LEVEL_3 necesitan un selector de padre (parentId obligatorio en el backend).
    // La UX de ese selector se define en el brainstorm #3-a; hasta entonces solo LEVEL_1 se renderiza.
    if (level !== 'LEVEL_1') {
        return (
            <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
                La gestión de <strong>{levelDisplayName}</strong> requiere seleccionar primero una
                entidad del nivel superior. El selector de padre se define en el diseño de las
                pantallas de nivel (pendiente).
            </div>
        );
    }

    return (
        <LevelEntitiesTable
            tenantId={tenantId}
            level={level}
            levelDisplayName={levelDisplayName}
            requireExternalId={requireExternalId}
        />
    );
}
